import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { CalendarDays, MapPin, ArrowRight, Clock } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import ImageHelper from '../lib/imageHelper';

interface Event {
    id: number;
    title: string;
    slug: string; // Added slug to interface
    description: string;
    start_date: string;
    photo?: string;
}

const UpcomingEvents = () => {
    const [events, setEvents] = useState<Event[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        axios
            .get('https://api.ibadansouthanglicandiocese.org.ng/api/v1/upcoming_events')
            .then((res) => {
                const filtered = res.data.events.filter((event: Event) => {
                    return new Date(event.start_date) >= new Date();
                });
                setEvents(filtered);
            })
            .catch((err) => console.error('Failed to fetch events:', err))
            .finally(() => setLoading(false));
    }, []);

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return {
            day: date.getDate(),
            month: date.toLocaleDateString('en-US', { month: 'short' }),
            full: date.toLocaleDateString('en-US', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
            })
        };
    };

    return (
        <section className="py-24 bg-[#fcfaf7]">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                
                {/* Section Header */}
                <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-6">
                    <div className="max-w-2xl">
                        <div className="flex items-center gap-2 text-amber-600 mb-4 uppercase tracking-[0.3em] text-[10px] font-bold">
                            <CalendarDays size={16} />
                            <span>Diocesan Calendar</span>
                        </div>
                        <h2 className="text-4xl md:text-5xl font-serif font-bold text-slate-900">
                            Upcoming Gatherings
                        </h2>
                        <p className="text-slate-500 font-serif italic mt-4 text-lg">
                            Join us as we fellowship, grow, and serve together in the presence of the Lord.
                        </p>
                    </div>
                    <Link to="/events">
                        <motion.button 
                            whileHover={{ x: 5 }}
                            className="group flex items-center gap-2 text-slate-900 font-bold uppercase tracking-widest text-[10px] border-b-2 border-amber-500 pb-1"
                        >
                            View Full Calendar <ArrowRight size={14} className="text-amber-500" />
                        </motion.button>
                    </Link>
                </div>

                {loading ? (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {[1, 2, 3].map((n) => (
                            <div key={n} className="h-[400px] bg-slate-100 rounded-[2.5rem] animate-pulse" />
                        ))}
                    </div>
                ) : events.length === 0 ? (
                    <motion.div 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="text-center py-20 bg-white rounded-[3rem] border border-dashed border-slate-200"
                    >
                        <CalendarDays size={48} className="mx-auto text-slate-200 mb-4" />
                        <h3 className="text-xl font-serif font-bold text-slate-900">No Scheduled Events</h3>
                        <p className="text-slate-500 italic">Check back soon for new updates.</p>
                    </motion.div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
                        {events.map((event, index) => {
                            const dateParts = formatDate(event.start_date);
                            return (
                                <motion.div
                                    key={event.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: index * 0.1 }}
                                >
                                    <Link to={`/events/${event.slug}`} className="block h-full group">
                                        <Card className="border-none bg-transparent shadow-none h-full transition-all">
                                            <CardContent className="p-0 flex flex-col h-full">
                                                {/* Image Container */}
                                                <div className="relative h-72 rounded-[2.5rem] overflow-hidden shadow-xl shadow-slate-200/50">
                                                    {event.photo ? (
                                                        <ImageHelper 
                                                            src={event.photo} 
                                                            alt={event.title} 
                                                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" 
                                                        />
                                                    ) : (
                                                        <div className="w-full h-full bg-slate-900 flex items-center justify-center">
                                                            <Church className="text-white/10 w-20 h-20" />
                                                        </div>
                                                    )}
                                                    
                                                    {/* Date Badge overlay */}
                                                    <div className="absolute top-6 left-6 bg-white/90 backdrop-blur-md rounded-2xl p-3 text-center shadow-lg min-w-[60px]">
                                                        <span className="block text-2xl font-bold text-slate-900 leading-none">{dateParts.day}</span>
                                                        <span className="block text-[10px] font-bold uppercase tracking-widest text-amber-600 mt-1">{dateParts.month}</span>
                                                    </div>

                                                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end p-8">
                                                        <span className="text-white text-xs font-bold uppercase tracking-widest flex items-center gap-2">
                                                            View Details <ArrowRight size={14} />
                                                        </span>
                                                    </div>
                                                </div>

                                                {/* Content Area */}
                                                <div className="pt-8 px-2 flex flex-col flex-grow">
                                                    <div className="flex items-center gap-4 text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-4">
                                                        <span className="flex items-center gap-1.5"><Clock size={12} className="text-amber-500" /> 10:00 AM</span>
                                                        <span className="flex items-center gap-1.5"><MapPin size={12} className="text-amber-500" /> Cathedral</span>
                                                    </div>
                                                    
                                                    <h3 className="text-2xl font-serif font-bold text-slate-900 group-hover:text-amber-600 transition-colors mb-3">
                                                        {event.title}
                                                    </h3>
                                                    
                                                    <p className="text-slate-500 text-sm leading-relaxed font-serif line-clamp-2 italic mb-6">
                                                        {event.description}
                                                    </p>
                                                    
                                                    <div className="mt-auto pt-6 border-t border-slate-100 flex items-center justify-between">
                                                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter">
                                                            {dateParts.full}
                                                        </span>
                                                        <div className="w-10 h-10 rounded-full border border-slate-200 flex items-center justify-center text-slate-400 group-hover:bg-slate-900 group-hover:text-white group-hover:border-slate-900 transition-all">
                                                            <ArrowRight size={16} />
                                                        </div>
                                                    </div>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    </Link>
                                </motion.div>
                            );
                        })}
                    </div>
                )}
            </div>
        </section>
    );
};

const Church = ({ className }: { className?: string }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
        <path d="M3 21h18M10 21V10l-2 2M14 21V10l2 2M5 21V7l7-5 7 5v14" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

export default UpcomingEvents;